import React, { useEffect, useMemo } from 'react'
import { Box, Grid, Skeleton, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { AdCopy, AdCopyCreationStatus, AdCopyStatus } from '../../../../__generated__/graphql'
import { displayDateAsText } from '../../../../utils/displayDateAsText'
import theme from '../../../../styles/theme'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'

const StyledImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact, theme }) => ({
  width: compact ? 120 : undefined,
  maxWidth: compact ? 120 : 150,
  height: compact ? 76 : 'auto',
  marginRight: theme.spacing(compact ? 1.5 : 1),
  border: compact ? `1px solid ${theme.palette.divider}` : undefined,
  borderRadius: compact ? theme.shape.borderRadius : undefined,
  objectFit: compact ? 'cover' : undefined,
}))

const ImagePlaceholder = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact, theme }) => ({
  width: compact ? 120 : 150,
  height: compact ? 76 : 80,
  marginRight: theme.spacing(compact ? 1.5 : 1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px dashed ${theme.palette.grey[400]}`,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.grey[100],
  fontSize: 12,
}))

const MaxGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  width: compact ? 360 : 340,
  minWidth: 0,
}))

type AdCopyPreviewCellProps = {
  item: Pick<AdCopy, 'name' | 'advertiserName' | 'title' | 'description' | 'callToAction'> & {
    statusUpdatedAt?: string
    status?: AdCopyStatus | AdCopyCreationStatus
    image?: string
    file?: File | null
  }
  compact?: boolean
}

type StatusSinceProps = {
  compact?: boolean
  status?: AdCopyStatus | AdCopyCreationStatus | null
  since?: string | null
}

const getPreviewText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

export const StatusSince: React.FC<StatusSinceProps> = ({
  compact = false,
  status,
  since,
}): React.ReactElement | null => {
  const t = useAppTranslations()

  if (!status && !since) return null
  const sinceLabel = since ? displayDateAsText(since, 'DD/MM/YYYY') : null

  return (
    <Typography variant="caption" color="textSecondary" component="p" sx={{ ml: theme.spacing(compact ? 0.75 : 1) }}>
      {sinceLabel ? `${status} ${t('adcopy.previewTable.since')} ${sinceLabel}` : null}
    </Typography>
  )
}

export const SkeletonPreview: React.FC = (): React.ReactElement => {
  return (
    <Grid
      container
      sx={{
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}
    >
      <Grid
        sx={{
          width: '10%',
        }}
      >
        <Skeleton variant="text" animation={false} />
      </Grid>
      <Grid container direction="row" wrap="nowrap">
        <Grid
          container
          size="auto"
          sx={{
            alignContent: 'center',
          }}
        >
          <Skeleton variant="rectangular" width={150} height={80} animation={false} sx={{ mr: 1 }} />
        </Grid>
        <MaxGrid>
          <Skeleton variant="text" animation={false} width="40%" />
          <Skeleton variant="text" animation={false} />
          <Skeleton variant="text" animation={false} width="80%" />
          <Skeleton variant="text" animation={false} width="30%" />
        </MaxGrid>
      </Grid>
    </Grid>
  )
}

export const AdCopyPreviewCell: React.FC<AdCopyPreviewCellProps> = React.memo(
  ({ compact = false, item }): React.ReactElement => {
    const t = useAppTranslations()
    const fileObjectUrl = useMemo(() => {
      if (!item?.file) return ''
      return URL.createObjectURL(item.file)
    }, [item?.file?.lastModified, item?.file?.name, item?.file?.size])

    useEffect(() => {
      if (!fileObjectUrl) return undefined
      return () => URL.revokeObjectURL(fileObjectUrl)
    }, [fileObjectUrl])

    const imageURL = item?.image ?? fileObjectUrl
    const hasImage = Boolean(imageURL)
    const name = getPreviewText(item.name)
    const advertiserName = getPreviewText(item.advertiserName)
    const title = getPreviewText(item.title)
    const description = getPreviewText(item.description)
    const callToAction = getPreviewText(item.callToAction)

    return (
      <Grid
        container
        sx={{
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            alignItems: 'center',
            mb: compact ? 0.5 : 0,
            width: '100%',
          }}
        >
          <Typography
            variant={compact ? 'body2' : 'caption'}
            component="p"
            sx={compact ? { color: 'text.primary', fontWeight: 700, lineHeight: 1.2 } : undefined}
          >
            {name}
          </Typography>
          <StatusSince compact={compact} status={item.status} since={item.statusUpdatedAt} />
        </Grid>
        <Grid container direction="row" wrap="nowrap" sx={{ width: '100%' }}>
          <Grid
            container
            size="auto"
            sx={{
              alignContent: 'center',
            }}
          >
            {hasImage ? (
              <StyledImage compact={compact} src={imageURL} aria-label={`image adcopy ${name}`} />
            ) : (
              <ImagePlaceholder compact={compact} aria-label={`missing image adcopy ${name}`}>
                {t('adcopy.noImage')}
              </ImagePlaceholder>
            )}
          </Grid>
          <MaxGrid compact={compact}>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={compact ? { lineHeight: 1.2, mb: 0.25 } : undefined}
            >
              {advertiserName}
            </Typography>
            <Typography
              variant={compact ? 'subtitle2' : 'h6'}
              component="p"
              sx={
                compact
                  ? {
                      lineHeight: 1.25,
                      mb: 0.25,
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                    }
                  : undefined
              }
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={
                compact
                  ? {
                      color: 'text.secondary',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                    }
                  : undefined
              }
            >
              {description}
            </Typography>
            <Typography variant="body2" color="secondary" sx={compact ? { lineHeight: 1.3 } : undefined}>
              {callToAction}
            </Typography>
          </MaxGrid>
        </Grid>
      </Grid>
    )
  }
)
